import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { BarChart3, Grid, AlertTriangle } from 'lucide-react';

/**
 * Issue Heatmap: Shows which slides have which types of issues
 * Color-coded by severity (now supports multi-dimensional unified analysis)
 */
const IssueHeatmap = ({ slides, unifiedAnalysis }) => {
  if (!slides || slides.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No slide data available for heatmap
      </div>
    );
  }

  // Use unified heatmap data if available
  const heatmapData = unifiedAnalysis?.heatmap_data || [];
  const hasUnifiedData = heatmapData.length > 0;

  // Calculate issue severity for each slide
  const getIssueSeverity = (slide) => {
    // PRIORITY 1: Use design_score from heatmap for accurate color representation
    // Now that design scores have context-aware baselines, they accurately reflect quality
    if (hasUnifiedData) {
      const slideNum = slide.slide_number || slides.indexOf(slide) + 1;
      const heatmapEntry = heatmapData.find(h => h.slide_number === slideNum);

      if (heatmapEntry && heatmapEntry.design_score !== undefined) {
        const score = heatmapEntry.design_score;
        // Convert score (0-100) to severity (0-5) with proper gradient
        // 80-100: dark green (0), 60-80: light green (1), 40-60: yellow (2), 20-40: orange (3), 0-20: red (4-5)
        if (score >= 80) return 0; // Dark green
        if (score >= 60) return 1; // Light green
        if (score >= 40) return 2; // Yellow
        if (score >= 20) return 3; // Orange
        return 4; // Red (0-20)
      }
    }

    // PRIORITY 2: Fall back to design-only score if unified data not available
    // Note: This is just design quality (layout+typography+proximity), not strategic
    if (slide.design_quality?.scores?.overall !== undefined) {
      const score = slide.design_quality.scores.overall;
      // Convert score (0-100) to severity (0-5)
      if (score >= 85) return 0; // Excellent
      if (score >= 75) return 1; // Good
      if (score >= 65) return 2; // Fair
      if (score >= 55) return 3; // Needs work
      if (score >= 45) return 4; // Poor
      return 5; // Critical
    }

    // Fallback to legacy calculation
    let severity = 0;

    // Check text density
    const text = slide.text_content || '';
    const wordCount = text.split(' ').length;
    if (wordCount > 150) severity += 3;
    else if (wordCount > 100) severity += 2;
    else if (wordCount > 75) severity += 1;

    // Check for lack of visuals
    const shapesCount = slide.shapes_count || 0;
    if (shapesCount === 0) severity += 2;
    else if (shapesCount < 2) severity += 1;

    // Check for recommendations (indicates issues)
    const recs = slide.recommendations || {};
    const totalRecs = (recs.content_refinements || []).length + (recs.visual_content || []).length;
    if (totalRecs > 5) severity += 2;
    else if (totalRecs > 3) severity += 1;

    return Math.min(5, severity); // Cap at 5
  };

  const getSeverityColor = (severity) => {
    if (severity === 0) return 'bg-green-100 dark:bg-green-900/20';
    if (severity === 1) return 'bg-green-200 dark:bg-green-800/30';
    if (severity === 2) return 'bg-yellow-200 dark:bg-yellow-800/30';
    if (severity === 3) return 'bg-orange-300 dark:bg-orange-700/40';
    if (severity === 4) return 'bg-red-300 dark:bg-red-700/40';
    return 'bg-red-400 dark:bg-red-600/50';
  };

  // Get detailed info for tooltip
  const getSlideDetails = (slide) => {
    const slideNum = slide.slide_number || slides.indexOf(slide) + 1;

    // Use actual slide-level scores if available
    if (slide.design_quality?.scores) {
      const designScore = Math.round(slide.design_quality.scores.overall || 0);
      const recs = slide.recommendations || {};
      const issueCount = (recs.content_refinements || []).length + (recs.visual_content || []).length;

      return {
        combined: designScore,
        design: designScore,
        content: designScore, // We don't have separate content score at slide level
        issues: issueCount
      };
    }

    // Fallback to heatmap data
    const heatmapEntry = heatmapData.find(h => h.slide_number === slideNum);
    if (heatmapEntry) {
      return {
        combined: heatmapEntry.combined_score,
        design: heatmapEntry.design_score,
        content: heatmapEntry.content_score,
        issues: heatmapEntry.issue_count
      };
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-xs">
        <span className="text-muted-foreground">Issue Severity:</span>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 dark:bg-green-900/20 border border-border"></div>
          <span>None</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-200 dark:bg-yellow-800/30 border border-border"></div>
          <span>Medium</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-400 dark:bg-red-600/50 border border-border"></div>
          <span>High</span>
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="grid grid-cols-10 gap-2">
        {slides.map((slide, index) => {
          const severity = getIssueSeverity(slide);
          const slideNum = slide.slide_number || index + 1;
          const details = getSlideDetails(slide);

          return (
            <div
              key={index}
              className={`
                relative aspect-square rounded border border-border
                ${getSeverityColor(severity)}
                hover:ring-2 hover:ring-accent transition-all
                cursor-pointer group
              `}
              title={`Slide ${slideNum}: ${severity} issue(s)`}
            >
              <div className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                {slideNum}
              </div>

              {/* Enhanced Tooltip on hover */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                <div className="bg-popover border border-border rounded px-3 py-2 text-xs shadow-lg min-w-[160px]">
                  <div className="font-semibold mb-1">Slide {slideNum}</div>
                  {details ? (
                    <div className="space-y-1 text-muted-foreground">
                      <div>Combined: <span className="font-medium text-foreground">{details.combined}</span></div>
                      <div>Design: <span className="font-medium text-blue-600 dark:text-blue-400">{details.design}</span></div>
                      <div>Content: <span className="font-medium text-green-600 dark:text-green-400">{details.content}</span></div>
                      <div className="text-[10px] mt-1 pt-1 border-t border-border">
                        {details.issues} recommendation(s)
                      </div>
                    </div>
                  ) : (
                    <div className="text-muted-foreground">
                      {severity === 0 ? 'No issues' : `${severity} issue(s)`}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        {hasUnifiedData
          ? 'Each cell shows combined design + content score. Hover for detailed breakdown.'
          : 'Each cell represents a slide. Darker colors indicate more issues requiring attention.'}
      </p>
    </div>
  );
};

/**
 * Top 3 Issue Categories: Shows the most common issues across the presentation
 */
const Top3IssueCategories = ({ slides }) => {
  if (!slides || slides.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No slide data available for issue analysis
      </div>
    );
  }

  // Check if slides have the required data
  const hasRequiredData = slides.some(slide =>
    slide.text_content !== undefined || slide.recommendations !== undefined
  );

  if (!hasRequiredData) {
    return (
      <div className="text-sm text-muted-foreground space-y-2">
        <p>Chart unavailable for this analysis.</p>
        <p className="text-xs">
          This is an older analysis. Please use the <strong>Re-Analyze</strong> button to generate fresh insights.
        </p>
      </div>
    );
  }

  // Analyze issues across all slides
  const issueCount = {
    lackOfVisuals: { count: 0, label: 'Lack of Visuals', description: 'slides with few or no visual elements', icon: '📊', color: 'bg-red-500' },
    tooMuchText: { count: 0, label: 'Too Much Text', description: 'slides with excessive text density', icon: '📝', color: 'bg-orange-500' },
    typos: { count: 0, label: 'Spelling/Grammar', description: 'slides with potential typos or errors', icon: '✍️', color: 'bg-yellow-500' },
    poorStructure: { count: 0, label: 'Poor Structure', description: 'slides with unclear messaging', icon: '🏗️', color: 'bg-blue-500' },
    inconsistentDesign: { count: 0, label: 'Inconsistent Design', description: 'slides with design inconsistencies', icon: '🎨', color: 'bg-purple-500' }
  };

  slides.forEach(slide => {
    const text = slide.text_content || '';
    const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
    const shapesCount = slide.shapes_count || 0;
    const recs = slide.recommendations || {};
    const contentRecs = recs.content_refinements || [];
    const visualRecs = recs.visual_content || [];

    // Check for lack of visuals
    if (shapesCount < 2) {
      issueCount.lackOfVisuals.count++;
    }

    // Check for too much text
    if (wordCount > 100) {
      issueCount.tooMuchText.count++;
    }

    // Check for typos/grammar (look in recommendations)
    const hasTypos = contentRecs.some(rec =>
      rec.toLowerCase().includes('typo') ||
      rec.toLowerCase().includes('spelling') ||
      rec.toLowerCase().includes('grammar')
    );
    if (hasTypos) {
      issueCount.typos.count++;
    }

    // Check for poor structure (many content recommendations)
    if (contentRecs.length > 3) {
      issueCount.poorStructure.count++;
    }

    // Check for inconsistent design (many visual recommendations)
    if (visualRecs.length > 3) {
      issueCount.inconsistentDesign.count++;
    }
  });

  // Get top 3 issues by count
  const topIssues = Object.values(issueCount)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .filter(issue => issue.count > 0);

  if (topIssues.length === 0) {
    return (
      <div className="text-center py-8 space-y-2">
        <div className="text-4xl">🎉</div>
        <p className="text-lg font-semibold text-foreground">No Major Issues Found!</p>
        <p className="text-sm text-muted-foreground">Your presentation looks great</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {topIssues.map((issue, index) => {
        const percentage = (issue.count / slides.length) * 100;
        const barWidth = percentage; // Bar width should match percentage, not relative to max

        return (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{issue.icon}</span>
                <div>
                  <div className="font-semibold text-foreground">{issue.label}</div>
                  <div className="text-xs text-muted-foreground">{issue.count} {issue.description}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-foreground">{issue.count}</div>
                <div className="text-xs text-muted-foreground">{percentage.toFixed(0)}% of slides</div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-muted rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${issue.color}`}
                style={{ width: `${barWidth}%` }}
              ></div>
            </div>
          </div>
        );
      })}

      <p className="text-xs text-muted-foreground text-center mt-6">
        Focus on addressing these common issues to improve your presentation quality.
      </p>
    </div>
  );
};

/**
 * Export the Top3IssueCategories for use in metric cards
 */
export { Top3IssueCategories };

/**
 * Main component combining both visualizations
 */
export default function PresentationInsightsCharts({ analysis, showOnlyHeatmap = false }) {
  const slides = analysis?.slides || [];
  const unifiedAnalysis = analysis?.unified_analysis;

  // If showOnlyHeatmap is true, return just the heatmap without the card wrapper
  if (showOnlyHeatmap) {
    return <IssueHeatmap slides={slides} unifiedAnalysis={unifiedAnalysis} />;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Issue Heatmap */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Grid className="w-5 h-5 text-prezlab-mint" />
            {unifiedAnalysis ? 'Multi-Dimensional Heatmap' : 'Issue Heatmap'}
          </CardTitle>
          <CardDescription>
            {unifiedAnalysis
              ? 'Combined design + content quality per slide'
              : 'Visual overview of issues across all slides'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <IssueHeatmap slides={slides} unifiedAnalysis={unifiedAnalysis} />
        </CardContent>
      </Card>

      {/* Top 3 Issue Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-prezlab-coral" />
            Top Issue Categories
          </CardTitle>
          <CardDescription>
            Most common issues in your presentation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Top3IssueCategories slides={slides} />
        </CardContent>
      </Card>
    </div>
  );
}
